// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import { useEvolveGet, type Shipment, type ShipmentId } from '@evolvemep/api-ts';
import { LoadingOverlay } from '@mantine/core';
import { useParams } from 'react-router';

import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNil } from 'helpers/isNotNil';
import { ShipmentModalProvider } from 'modules/Shop/Shipments/modalRegistries';

export type ShipmentDetailPageProps = {
  shipment: Shipment;
  onShipmentUpdated: (shipment: Shipment) => void;
};

type Props = {
  children: (context: ShipmentDetailPageProps) => React.ReactNode;
};

export const ShipmentDetailWrapper = ({ children }: Props) => {
  const navigate = useEvolveNavigate('', true);
  const [shipment, setShipment] = useState<Shipment | null>(null);

  const { shipmentId } = useParams() as { shipmentId?: ShipmentId };

  const { apiCall: getShipment, loading: loadingShipment } = useEvolveGet('shop/shipment/:shipmentId');
  const shipmentMatches = useMemo(() => shipment?.shipmentId === shipmentId, [shipment?.shipmentId, shipmentId]);

  useEffect(() => {
    if (isNil(shipmentId)) {
      void navigate('/shipping-orders');
    } else if (!shipmentMatches) {
      getShipment({ url: `shop/shipment/${shipmentId}` })
        .then(setShipment)
        .catch(() => {
          void navigate('/shipping-orders');
        });
    }
  }, [getShipment, navigate, shipmentMatches, shipmentId]);

  if (isNil(shipment) || loadingShipment || !shipmentMatches) {
    return <LoadingOverlay visible />;
  }
  return <ShipmentModalProvider>{children({ shipment, onShipmentUpdated: setShipment })}</ShipmentModalProvider>;
};
